<script lang="ts" setup>
import { RouterLink } from 'vue-router'
import { onMounted, onUnmounted, ref } from 'vue'

import Copyright from '@/components/layout/Copyright.vue'
import LivreroLogo from '@/components/LivreroLogo.vue'
import LocaleDropdown from '@/components/LocaleDropdown.vue'

const particles = ref<Array<{ id: number; x: number; y: number; size: number; speed: number }>>([])
const mousePosition = ref({ x: 0, y: 0 })
const containerRef = ref<HTMLElement | null>(null)

function updateMousePosition(event: MouseEvent) {
    if (containerRef.value) {
        const rect = containerRef.value.getBoundingClientRect()
        mousePosition.value = {
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100,
        }
    }
}

function getParticleConnections() {
    const maxDistance = 25 // Maximum distance for connection (in percentage)
    const connections: Array<{ particle: (typeof particles.value)[0]; opacity: number }> = []

    particles.value.forEach((particle) => {
        const distance = Math.sqrt(
            Math.pow(particle.x - mousePosition.value.x, 2) +
                Math.pow(particle.y - mousePosition.value.y, 2),
        )

        if (distance <= maxDistance) {
            const opacity = 1 - distance / maxDistance // Closer = more opaque
            connections.push({ particle, opacity: opacity * 0.4 }) // Max opacity 0.4 for subtlety
        }
    })

    return connections
}

onMounted(() => {
    // Generate floating particles (slightly larger and denser for visibility)
    for (let i = 0; i < 80; i++) {
        particles.value.push({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 6 + 2,
            speed: Math.random() * 1.5 + 0.5,
        })
    }

    // Add mouse tracking
    if (containerRef.value) {
        containerRef.value.addEventListener('mousemove', updateMousePosition)
    }

    // Animate particles
    setInterval(() => {
        particles.value.forEach((particle) => {
            particle.y -= particle.speed * 0.1
            if (particle.y < -10) {
                particle.y = 110
                particle.x = Math.random() * 100
            }
        })
    }, 50)
})

onUnmounted(() => {
    if (containerRef.value) {
        containerRef.value.removeEventListener('mousemove', updateMousePosition)
    }
})
</script>

<template>
    <div ref="containerRef" class="min-h-screen w-full overflow-hidden relative">
        <!-- Subtle brand background -->
        <div
            class="absolute inset-0 bg-gradient-to-br from-primary-100 via-primary-50 to-primary-100 dark:from-dark-600 dark:via-dark-700 dark:to-dark-600"
        >
            <!-- Soft mesh accents -->
            <div class="absolute inset-0 opacity-30">
                <div
                    class="absolute -top-1/3 -right-1/3 w-[70%] h-[70%] bg-gradient-conic from-yellow-200 via-yellow-100 to-primary-50 dark:from-yellow-500/20 dark:via-yellow-400/10 dark:to-dark-700 rounded-full blur-3xl"
                ></div>
                <div
                    class="absolute -bottom-1/3 -left-1/3 w-[70%] h-[70%] bg-gradient-conic from-yellow-300 via-primary-50 to-yellow-100 dark:from-yellow-600/20 dark:via-dark-700 dark:to-yellow-400/10 rounded-full blur-3xl"
                ></div>
            </div>

            <!-- Floating Particles -->
            <div class="absolute inset-0 overflow-hidden">
                <div
                    v-for="particle in particles"
                    :key="particle.id"
                    class="absolute bg-yellow-400/30 dark:bg-yellow-500/20 ring-1 ring-yellow-300/25 dark:ring-yellow-400/25 rounded-full shadow-sm"
                    :style="{
                        left: particle.x + '%',
                        top: particle.y + '%',
                        width: particle.size + 'px',
                        height: particle.size + 'px',
                        animationDelay: particle.id * 0.1 + 's',
                    }"
                />

                <!-- Connection Lines SVG -->
                <svg class="absolute inset-0 w-full h-full pointer-events-none">
                    <line
                        v-for="connection in getParticleConnections()"
                        :key="`connection-${connection.particle.id}`"
                        :x1="connection.particle.x + '%'"
                        :y1="connection.particle.y + '%'"
                        :x2="mousePosition.x + '%'"
                        :y2="mousePosition.y + '%'"
                        stroke="#FFD128"
                        :stroke-opacity="connection.opacity"
                        stroke-width="1"
                        class="transition-opacity duration-200"
                    />
                </svg>
            </div>

            <!-- Noise Texture Overlay -->
            <div class="absolute inset-0 bg-noise opacity-20 mix-blend-overlay"></div>
            <!-- Gentle vertical vignette to avoid flat backgrounds -->
            <div
                class="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-primary-50/40 to-primary-100/50 dark:via-dark-700/40 dark:to-dark-800/50"
            ></div>
        </div>

        <!-- Main Content Grid -->
        <div class="relative z-10 grid lg:grid-cols-2 min-h-screen">
            <!-- Brand Side (Left) -->
            <div class="hidden lg:flex flex-col justify-center px-12 xl:px-20">
                <div class="max-w-lg">
                    <LivreroLogo class="mb-12" />

                    <!-- Welcome Message -->
                    <!--                    <div class="space-y-6">-->
                    <!--                        <h2 class="text-3xl font-light text-white/90 leading-relaxed">-->
                    <!--                            Welcome to the future of-->
                    <!--                            <span-->
                    <!--                                class="font-semibold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent"-->
                    <!--                            >-->
                    <!--                                logistics management-->
                    <!--                            </span>-->
                    <!--                        </h2>-->

                    <!--                        <p class="text-lg text-white/70 leading-relaxed">-->
                    <!--                            Experience seamless transportation coordination with our cutting-edge-->
                    <!--                            platform designed for modern logistics.-->
                    <!--                        </p>-->

                    <!--                        &lt;!&ndash; Feature Highlights &ndash;&gt;-->
                    <!--                        <div class="space-y-4 mt-8">-->
                    <!--                            <div class="flex items-center space-x-3 text-white/80">-->
                    <!--                                <div class="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>-->
                    <!--                                <span>Real-time tracking & monitoring</span>-->
                    <!--                            </div>-->
                    <!--                            <div class="flex items-center space-x-3 text-white/80">-->
                    <!--                                <div-->
                    <!--                                    class="w-2 h-2 bg-purple-400 rounded-full animate-pulse"-->
                    <!--                                    style="animation-delay: 0.5s"-->
                    <!--                                ></div>-->
                    <!--                                <span>Advanced analytics & reporting</span>-->
                    <!--                            </div>-->
                    <!--                            <div class="flex items-center space-x-3 text-white/80">-->
                    <!--                                <div-->
                    <!--                                    class="w-2 h-2 bg-pink-400 rounded-full animate-pulse"-->
                    <!--                                    style="animation-delay: 1s"-->
                    <!--                                ></div>-->
                    <!--                                <span>Seamless integration ecosystem</span>-->
                    <!--                            </div>-->
                    <!--                        </div>-->
                    <!--                    </div>-->
                </div>
            </div>

            <!-- Login Form Side (Right) -->
            <div class="flex flex-col justify-center items-center px-6 sm:px-12 relative">
                <!-- Top Controls -->
                <div class="absolute right-6 top-6 flex gap-3 z-20">
                    <RouterLink
                        v-if="$route.name !== 'login'"
                        class="flex items-center px-4 py-2 text-sm font-medium text-dark-500 hover:text-yellow-500 dark:text-dark-200 dark:hover:text-yellow-400 bg-white dark:bg-dark-800 border border-dark-400 dark:border-dark-400 rounded-xl transition-all duration-300 hover:border-yellow-400 dark:hover:border-yellow-500"
                        to="/login"
                    >
                        <MdiLogin class="w-4 h-4 mr-2" />
                        Login
                    </RouterLink>
                    <div
                        class="bg-white dark:bg-dark-800 border border-dark-400 dark:border-dark-400 rounded-xl p-1"
                    >
                        <LocaleDropdown placement="bottom-right" />
                    </div>
                </div>

                <!-- Login Form Container -->
                <div class="w-full max-w-md relative">
                    <!-- Glass Morphism Background -->
                    <div
                        class="absolute inset-0 bg-white/90 dark:bg-dark-800/90 backdrop-blur-xl border border-white/20 dark:border-dark-400/30 rounded-3xl shadow-2xl dark:shadow-dark-900/50"
                    ></div>

                    <!-- Form Content -->
                    <div class="relative p-8">
                        <slot />
                    </div>
                </div>

                <!-- Copyright -->
                <div class="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                    <Copyright class="text-dark-300 dark:text-dark-300" />
                </div>
            </div>
        </div>

        <!-- Mobile Brand Header -->
        <!--        <div class="lg:hidden absolute top-0 left-0 right-0 p-6 z-10">-->
        <!--            <div class="text-center">-->
        <!--                <h1 class="text-4xl font-bold text-dark-500 dark:text-dark-200">Livrero</h1>-->
        <!--                <p class="text-dark-300 dark:text-dark-300 text-sm mt-2">-->
        <!--                    Next-gen logistics platform-->
        <!--                </p>-->
        <!--            </div>-->
        <!--        </div>-->
    </div>
</template>

<style scoped>
@keyframes spin-slow {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@keyframes gradient {
    0%,
    100% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
}

.animate-spin-slow {
    animation: spin-slow 20s linear infinite;
}

.animate-gradient {
    animation: gradient 3s ease-in-out infinite;
    background-size: 200% 200%;
}

.bg-noise {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.4'/%3E%3C/svg%3E");
}
</style>
